import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import VideoPlayer from "@/pages/video/components/VideoPlayer";
import RelatedVideoList from "@/pages/video/components/RelatedVideoList";
import { categories } from "@/mocks/videos";
import {
  formatDate,
  formatDuration,
  formatViews,
  getCategoryName,
  getLatestVideos,
  getRelatedVideos,
  getVideo,
} from "@/lib/videos";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function VideoDetailPage() {
  const { videoId = "" } = useParams();
  const video = getVideo(videoId);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: video ? `${video.title} - 茄子视频` : "视频不存在 - 茄子视频",
    description: video
      ? `${video.description.slice(0, 80)} 关键词：${video.tags.join("、")}。在茄子视频免费在线观看。`
      : "该视频可能已下架，返回茄子视频首页发现更多精彩短视频。",
    keywords: video
      ? `${video.title},${video.tags.join(",")},茄子视频,短视频在线观看`
      : "茄子视频,短视频",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!video) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[900px] px-4 py-16 md:px-8">
          <EmptyState
            icon="ri-movie-line"
            title="视频不存在或已下架"
            description="你访问的链接可能已经失效，可以回到首页看看今天的新内容。"
          >
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/category/entertainment"
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览分类
            </Link>
          </EmptyState>
        </div>
      </SiteLayout>
    );
  }

  const categoryName = getCategoryName(video.categoryId);
  const related = getRelatedVideos(video, 6);
  const favorited = isFavorite(video.id);
  const fallbackRelated = getLatestVideos(4);

  const handleFavorite = () => {
    const next = toggle(video.id);
    setToast(next ? "已加入收藏，可在收藏页查看" : "已从收藏中移除");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("播放链接已复制，去分享给朋友吧");
    } catch {
      try {
        const input = document.createElement("textarea");
        input.value = url;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setToast("播放链接已复制，去分享给朋友吧");
      } catch {
        setToast("复制失败，请手动复制浏览器地址栏链接");
      }
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 pt-6 md:px-8 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: categoryName, to: `/category/${video.categoryId}` },
            { label: video.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_356px]">
          <div className="min-w-0">
            <VideoPlayer video={video} />

            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${video.categoryId}`}
                  className="rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-medium text-primary-800 transition-colors hover:bg-primary-200"
                >
                  {categoryName}
                </Link>
                {video.tags.slice(0, 2).map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-xl font-extrabold leading-snug tracking-tight text-foreground-950 md:text-[26px]">
                {video.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 font-heading text-xs font-bold text-accent-800">
                    {video.author.slice(0, 1)}
                  </span>
                  <span className="font-medium text-foreground-800">{video.author}</span>
                </span>
                <span className="hidden h-1 w-1 rounded-full bg-background-300 sm:block"></span>
                <span>{formatDate(video.uploadTime)} 发布</span>
                <span className="hidden h-1 w-1 rounded-full bg-background-300 sm:block"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-list-2-line text-sm"></i>
                  {formatViews(video.views)} 播放
                </span>
                <span className="hidden h-1 w-1 rounded-full bg-background-300 sm:block"></span>
                <span>{formatDuration(video.duration)}</span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                  }`}
                >
                  <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-base`}></i>
                  {favorited ? "已收藏" : "收藏这条视频"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-share-forward-line text-base"></i>
                  分享链接
                </button>
                <Link
                  to="/favorites"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-xs text-foreground-500 transition-colors hover:text-primary-600"
                >
                  <i className="ri-bookmark-3-line text-base"></i>
                  查看我的收藏
                </Link>
              </div>

              <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-file-text-line text-lg text-primary-500"></i>
                  视频简介
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{video.description}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {video.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-user-3-line text-base text-foreground-400"></i>
                    作者：<span className="text-foreground-800">{video.author}</span>
                  </div>
                  <p className="font-label text-[11px] text-foreground-400">
                    本站视频与文字均为虚构演示内容
                  </p>
                </div>
              </div>
            </div>
          </div>

          <aside className="w-full">
            <div className="lg:sticky lg:top-24">
              <RelatedVideoList videos={related.length > 0 ? related : fallbackRelated} />

              <div className="mt-4 rounded-xl bg-primary-50 p-5">
                <h2 className="font-heading text-base font-bold text-primary-950">继续逛分类</h2>
                <p className="mt-1.5 text-xs leading-relaxed text-primary-800/80">
                  相关推荐基于分类与标签匹配，换个分类会有完全不同的内容。
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {categories.slice(0, 4).map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="flex cursor-pointer items-center gap-1.5 rounded-md bg-background-50/80 px-2.5 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700"
                    >
                      <i className={`${category.icon} text-sm text-primary-500`}></i>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-foreground-950 px-4 py-2.5 text-xs text-background-50">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-checkbox-circle-fill text-sm text-accent-400"></i>
            {toast}
          </span>
        </div>
      )}
    </SiteLayout>
  );
}